import {useForecastFetchQuery} from '../../../../shared/hooks/useForecastFetchQuery';
import {CalendarEventsQuery, CalendarEventType} from './CalendarEventsFetch';
import {useEffect, useMemo} from 'react';
import type {
	CalendarEventsFetch_Query$data as CalendarEventsFetch_QueryResponse,
	CalendarEventsFetch_Query$variables as CalendarEventsFetch_QueryVariables,
} from './__generated__/CalendarEventsFetch_Query.graphql';
import Moment from 'moment';
import {getWeek} from 'date-fns';

export type useCalendarEventsType = {
	calendarEvents: CalendarEventType[];
	isLoadingCalendarEvents: boolean;
};

export const useCalendarEvents = (
	currentViewingDate: Moment.Moment,
	currentViewingMonth: Moment.Moment,
	showCalendarEvents: boolean
): useCalendarEventsType => {
	const {
		fetch: fetchCalendarEvents,
		data: calendarEventsData,
		loading: isLoadingCalendarEvents,
	} = useForecastFetchQuery<CalendarEventsFetch_QueryResponse, CalendarEventsFetch_QueryVariables>(CalendarEventsQuery, true);

	const calendarEvents = useMemo(() => {
		if (!showCalendarEvents) return [];
		return (
			calendarEventsData?.viewer?.calendarIntegrations?.calendarEvents?.edges
				?.map(edge => edge?.node as CalendarEventType)
				.filter(event => event?.start && getWeek(new Date(event.start)) === currentViewingDate.week()) ?? []
		);
	}, [calendarEventsData?.viewer?.calendarIntegrations?.calendarEvents, showCalendarEvents, currentViewingDate.week()]);
	const startDate = currentViewingMonth.clone().startOf('month').startOf('week').format('YYYY-MM-DD');
	const endDate = currentViewingMonth.clone().endOf('month').endOf('week').format('YYYY-MM-DD');

	useEffect(() => {
		if (showCalendarEvents) {
			fetchCalendarEvents({
				start: startDate,
				end: endDate,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
		}
	}, [startDate, endDate, showCalendarEvents]);

	return {
		calendarEvents,
		isLoadingCalendarEvents,
	};
};
